<template>
  <div>
    <div>
      <div class="d-flex justify-space-between">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Your details
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="saveAndExit"
        />
      </div>
    </div>

    <!-- the form -->
    <ValidationObserver ref="observerFormDetails">
      <form @submit.prevent="onSubmit">
        <BaseLabel :text="LABEL_CONSTANTS.TITLE_LABEL" class="mt-10 mb-2" />
        <BaseSelect
          dense
          :vid="questionApplicant1Title.InterfaceKey"
          v-model="questionApplicant1Title.Value"
          :items="applicant1TitleOptions"
          :data-uiid="questionApplicant1Title.InterfaceKey"
          :isEnableSortOrderListItems="false"
          :hide-details="false"
          :selectedDefault="LABEL_CONSTANTS.SELECT_DEFAULT_PLACEHOLDER"
          :rules="{
            required: {
              required: true,
              fieldLabel: LABEL_CONSTANTS.TITLE_LABEL,
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '280px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Title"
        />

        <BaseLabel :text="LABEL_CONSTANTS.FIRST_NAME_LABEL" class="mt-3 mb-2" />
        <BaseInput
          :vid="questionApplicant1Forename.InterfaceKey"
          v-model="questionApplicant1Forename.Value"
          :data-uiid="questionApplicant1Forename.InterfaceKey"
          :hide-details="false"
          :rules="applicantFirstNameRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="First Name"
        />

        <BaseLabel :text="LABEL_CONSTANTS.LAST_NAME_LABEL" class="mt-3 mb-2" />
        <BaseInput
          :vid="questionApplicant1Surname.InterfaceKey"
          v-model="questionApplicant1Surname.Value"
          :data-uiid="questionApplicant1Surname.InterfaceKey"
          :hide-details="false"
          :rules="applicantLastNameRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Last Name"
        />

        <BaseLabel
          :text="LABEL_CONSTANTS.DATE_OF_BIRTH_LABEL"
          class="mt-3 mb-2"
        />
        <BaseInputDate
          :vid="questionApplicant1DoB.InterfaceKey"
          :data-uiid="questionApplicant1DoB.InterfaceKey"
          :value="questionApplicant1DoB.Value"
          v-model="questionApplicant1DoB.Value"
          :rules="applicantDateOfBirthRules"
          :hideDetails="false"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <BaseLabel
          :text="LABEL_CONSTANTS.FULL_TIME_STUDENT"
          :hintText="LABEL_CONSTANTS.FULL_TIME_STUDENT_HINT_TEXT"
          class="mt-3 mb-2"
        />
        <BaseToggleButton
          v-model="questionApplicant1Student.Value"
          :data-uiid="questionApplicant1Student.InterfaceKey"
          :toggleButtonText="{
            yesDataUiid: `${questionApplicant1Student.InterfaceKey}_Yes`,
            noDataUiid: `${questionApplicant1Student.InterfaceKey}_No`,
          }"
          styleClass="mb-9"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <BaseToggle
          v-model="questionSecondApplicantIncluded.Value"
          label="Do you wish to include a second policyholder?"
          pslStyle="button"
          class="mt-3 second-applicant-included-field"
          :style="{ 'max-width': $vuetify.breakpoint.mdAndDown ? '100%' : '' }"
          aria-label="Do you wish to include a second policyholder?"
        />
        <p class="sub-text mt-3 mb-9">
          This policy will cover everyone in your household, including you, your
          partner, including any children or foster children and any other
          person permanently living with you under your tenancy agreement. A
          second policy holder can make policy changes and make claims on behalf
          of the household. They must reside at the property and be named on the
          tenancy agreement.
        </p>

        <!-- second applicant form -->
        <template v-if="questionSecondApplicantIncluded.Value">
          <BaseLabel :text="LABEL_CONSTANTS.TITLE_LABEL" class="mt-3 mb-2" />
          <BaseSelect
            v-model="questionApplicant2Title.Value"
            :items="applicant2TitleOptions"
            :data-uiid="questionApplicant2Title.InterfaceKey"
            :isEnableSortOrderListItems="false"
            :hide-details="false"
            :selectedDefault="LABEL_CONSTANTS.SELECT_DEFAULT_PLACEHOLDER"
            :rules="{
              required: {
                required: true,
                fieldLabel: LABEL_CONSTANTS.TITLE_LABEL,
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '280px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Title"
          />

          <BaseLabel
            :text="LABEL_CONSTANTS.FIRST_NAME_LABEL"
            class="mt-3 mb-2"
          />
          <BaseInput
            v-model="questionApplicant2Forename.Value"
            :vid="questionApplicant2Forename.InterfaceKey"
            :data-uiid="questionApplicant2Forename.InterfaceKey"
            :hide-details="false"
            :rules="applicantFirstNameRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="First Name"
          />

          <BaseLabel
            :text="LABEL_CONSTANTS.LAST_NAME_LABEL"
            class="mt-3 mb-2"
          />
          <BaseInput
            v-model="questionApplicant2Surname.Value"
            :vid="questionApplicant2Surname.InterfaceKey"
            :data-uiid="questionApplicant2Surname.InterfaceKey"
            :hide-details="false"
            :rules="applicantLastNameRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Last Name"
          />

          <BaseLabel
            :text="LABEL_CONSTANTS.DATE_OF_BIRTH_LABEL"
            class="mt-3 mb-2"
          />
          <BaseInputDate
            :vid="questionApplicant2DoB.InterfaceKey"
            :data-uiid="questionApplicant2DoB.InterfaceKey"
            :value="questionApplicant2DoB.Value"
            :hideDetails="false"
            v-model="questionApplicant2DoB.Value"
            :rules="applicantDateOfBirthRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          />

          <BaseLabel
            :text="LABEL_CONSTANTS.FULL_TIME_STUDENT_2"
            :hintText="LABEL_CONSTANTS.FULL_TIME_STUDENT_HINT_TEXT_2"
            class="mt-3 mb-2"
          />
          <BaseToggleButton
            v-model="questionApplicant2Student.Value"
            :data-uiid="questionApplicant2Student.InterfaceKey"
            :toggleButtonText="{
              yesDataUiid: `${questionApplicant2Student.InterfaceKey}_Yes`,
              noDataUiid: `${questionApplicant2Student.InterfaceKey}_No`,
            }"
            styleClass="mb-10"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
        </template>

        <BaseLabel
          :text="LABEL_CONSTANTS.PREVIOUS_HELD"
          class="mt-3 mb-2"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '600px' : '100%',
          }"
        />
        <BaseToggleButton
          v-model="questionPreviousHeld.Value"
          :data-uiid="questionPreviousHeld.InterfaceKey"
          :toggleButtonText="{
            yesDataUiid: `${questionPreviousHeld.InterfaceKey}_Yes`,
            noDataUiid: `${questionPreviousHeld.InterfaceKey}_No`,
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          :rules="{
            required: {
              required: true,
            },
          }"
          styleClass="mb-10"
        />

        <template v-if="questionPreviousHeld.Value">
          <BaseLabel
            :text="LABEL_CONSTANTS.TENANTS_PREVIOUS_CLAIM_LAST_FIVE_YEAR"
            class="mt-3 mb-2"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '600px' : '100%',
            }"
          />
          <BaseToggleButton
            v-model="questionPreviousClaim.Value"
            :data-uiid="questionPreviousClaim.InterfaceKey"
            :toggleButtonText="{
              yesDataUiid: `${questionPreviousClaim.InterfaceKey}_Yes`,
              noDataUiid: `${questionPreviousClaim.InterfaceKey}_No`,
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
            :rules="{
              required: {
                required: true,
              },
            }"
          />

          <template v-if="questionPreviousClaim.Value">
            <div class="mt-6">
              <!-- PreviousClaimsForm contains an ValidationObserver -->
              <BaseAlert
                v-if="!isClaimFormValid"
                color="var(--red-100)"
                alertText="Please add your details of your claim below."
                alertTextClass="black--text font-16"
                icon="mdi-alert-circle"
                class="field-invalid v-messages error--text"
                iconColor="var(--red-500)"
              />

              <PreviousClaimsForm
                :hideDetails="false"
                :excludeFieldsClaim="EXCLUDE_FIELDS_CLAIM"
                :tableHeaders="tableClaimHeader"
                :previousClaimTypeOfLossOptions="
                  TENANTS_CONTENT_PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS
                "
                styleClass="claim-form-without-claim-under"
                @add-table-items="addTableItems"
                @remove-table-items="removeTableItems"
              />
            </div>
          </template>

          <BaseLabel
            :text="LABEL_CONSTANTS.NO_CLAIMS_CONTENTS"
            :hintText="LABEL_CONSTANTS.TENANTS_NO_CLAIMS_CONTENTS_HINT_TEXT"
            class="mt-10 mb-2"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '600px' : '100%',
            }"
          ></BaseLabel>
          <BaseRadio
            v-model="questionNoClaimsContents.Value"
            pslStyle="button"
            class="mb-10 radio-group__wrapper"
            data-uiid="NoClaimsContents"
            :hideDetails="false"
            :key="`${questionNoClaimsContents.Value}_NoClaimsContents`"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '120' : ''"
            :items="noClaimContentOptions"
            :rules="{
              required: {
                required: true,
                fieldLabel: LABEL_CONSTANTS.NO_CLAIMS_CONTENTS_LABEL_MSG,
              },
            }"
            aria-label="No Claims Contents"
          />
        </template>

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 primary--text"
          type="submit"
          :color="'psorange'"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
          :text="
            $vuetify.breakpoint.lgAndUp
              ? 'CONTINUE TO PROPERTY DETAILS'
              : 'CONTINUE'
          "
        />
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { populateClaimTable } from "@/common/claims";
import { EventBus } from "@/common/eventbus.js";
import {
  addFieldLabel,
  convertToBool,
  scrollTop,
  tryGoToNextStep,
} from "@/common/utils";
import { ROUTE_NAME } from "@/constants/route.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapActions, mapState } from "vuex";

import {
  APPLICANT_TITLE_ITEMS_PLUS_MX,
  NO_CLAIMS_CONTENTS_ITEMS,
  setItemDataUiids,
  TENANTS_CONTENT_PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS,
} from "@/constants/component.constants";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import {
  ERROR_INVALID,
  ERROR_MSG_MAX_CHARACTER,
  ERROR_NAME_INVALID,
} from "@/constants/message.constants.js";
import { DATE_REGEX, NAME_REGEX } from "@/constants/regex-patterns.constants";

import BaseAlert from "@/components/BaseComponents/BaseAlert";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseToggle from "@/components/BaseComponents/BaseToggle";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import PreviousClaimsForm from "@/components/Forms/PreviousClaimsForm";
import { ValidationObserver } from "vee-validate";
import BaseRadio from "@/components/BaseComponents/BaseRadio.vue";
import { numbersToWords } from "@/common/answers";

export default {
  name: "YourDetailsPage",
  components: {
    ValidationObserver,
    BaseRadio,
    BaseLabel,
    BaseToggleButton,
    BaseInput,
    BaseInputDate,
    BaseButton,
    BaseSelect,
    BaseToggle,
    BaseAlert,
    PreviousClaimsForm,
  },
  data() {
    return {
      questionApplicant1DoB: {},
      questionApplicant1Forename: {},
      questionApplicant1Surname: {},
      questionApplicant1Title: {},
      questionApplicant1Student: {},
      questionSecondApplicantIncluded: {},
      questionApplicant2DoB: {},
      questionApplicant2Forename: {},
      questionApplicant2Surname: {},
      questionApplicant2Title: {},
      questionApplicant2Student: {},
      questionPreviousHeld: {
        InterfaceKey: "HeldInsurance",
        Value: false,
      },
      questionPreviousClaim: {},
      questionNoClaimsContents: {},
      previousClaimItems: [],
      repeatingGroupPreviousClaim: {},
      isClaimFormValid: true,
      NAME_REGEX,
      DATE_REGEX,
      LABEL_CONSTANTS,
      ERROR_NAME_INVALID,
      ERROR_MSG_MAX_CHARACTER,
      ERROR_INVALID,
      APPLICANT_TITLE_ITEMS_PLUS_MX,
      TENANTS_CONTENT_PREVIOUS_CLAIM_TYPE_OF_LOSS_ITEMS,
      EXCLUDE_FIELDS_CLAIM: ["PreviousClaimClaimedUnder"],
      applicantDateOfBirthRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          date_valid: {
            errorMessageTemplate: ERROR_INVALID,
          },
          regex: {
            errorMessageTemplate: ERROR_INVALID,
            pattern: DATE_REGEX,
          },
          age_in_range: {
            min: 18,
            max: 120,
          },
        },
        LABEL_CONSTANTS.DATE_OF_BIRTH_LABEL
      ),
      applicantFirstNameRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 50,
            errorMessageTemplate: ERROR_MSG_MAX_CHARACTER,
          },
          min_length: {
            length: 2,
          },
          regex: {
            pattern: NAME_REGEX,
            customMessage: ERROR_NAME_INVALID,
          },
        },
        LABEL_CONSTANTS.FIRST_NAME_LABEL
      ),
      applicantLastNameRules: addFieldLabel(
        {
          required: {
            required: true,
          },
          max: {
            length: 100,
            errorMessageTemplate: ERROR_MSG_MAX_CHARACTER,
          },
          min_length: {
            length: 2,
          },
          regex: {
            pattern: NAME_REGEX,
            customMessage: ERROR_NAME_INVALID,
          },
        },
        LABEL_CONSTANTS.LAST_NAME_LABEL
      ),
      tableClaimHeader: [
        {
          text: "Claim",
          value: "PreviousClaimTypeOfLoss.Text",
          align: "start",
          sortable: false,
          interfaceKey: "PreviousClaimTypeOfLoss",
        },
        {
          text: "Date",
          value: "PreviousClaimDateOfLoss.Text",
          align: "start",
          sortable: false,
          interfaceKey: "PreviousClaimDateOfLoss",
        },
        {
          text: "Value",
          value: "PreviousClaimAmountOfLoss.Text",
          sortable: false,
          interfaceKey: "PreviousClaimAmountOfLoss",
        },
        {
          text: "",
          value: "actions",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "quoteResponse",
      "questionMap",
      "restoringLastStep",
    ]),
    noClaimContentOptions() {
      return setItemDataUiids("NoClaimsContents", NO_CLAIMS_CONTENTS_ITEMS);
    },
    applicant1TitleOptions() {
      return setItemDataUiids(
        "Applicant1Title",
        this.APPLICANT_TITLE_ITEMS_PLUS_MX
      );
    },
    applicant2TitleOptions() {
      return setItemDataUiids(
        "Applicant2Title",
        this.APPLICANT_TITLE_ITEMS_PLUS_MX
      );
    },
    isNoClaimValid() {
      const isNoClaimItems = !!this.repeatingGroupPreviousClaim?.Value?.length;

      // Valid if user added a claim, or said "No" and left fields empty
      const claimAdded = this.questionPreviousClaim.Value && isNoClaimItems;
      const claimNotAdded =
        !this.questionPreviousClaim.Value && !isNoClaimItems;
      return claimAdded || claimNotAdded;
    },
  },
  beforeMount() {
    this.onSetupAndLoadSavedData();
    scrollTop();
  },
  mounted() {
    this.setCurrentRefObserverForm(this.$refs.observerFormDetails);

    if (!this.restoringLastStep) {
      return;
    }

    // work around running validate before the data has been added to input
    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.observerFormDetails,
        async (form) => (await form.validate()) && this.isNoClaimValid,
        ROUTE_NAME.TENANTS_CONTENTS.B2B.PROPERTY_DETAILS
      );
    }, 0);
  },
  watch: {
    "questionPreviousClaim.Value"(value) {
      if (value !== undefined && !value) {
        this.isClaimFormValid = true;
        this.repeatingGroupPreviousClaim.Value = [];
        this.resetNoClaimsTable();
      }
    },
    "quoteResponse.Answers"() {
      this.populateClaimsValue();
    },
    "questionPreviousHeld.Value"(value) {
      if (!value) {
        this.questionNoClaimsContents.Value = numbersToWords(0);
        this.questionPreviousClaim.Value = false;
      }
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "saveProgress",
      "setIsExpiredFlag",
      "toggleGlobalSaveAndExitDialog",
      "setCurrentRefObserverForm",
      "refreshSessionAndNextStep",
    ]),

    onSetupAndLoadSavedData() {
      this.questionApplicant1DoB = this.questionMap.Applicant1DoB;
      this.questionApplicant1Forename = this.questionMap.Applicant1Forename;
      this.questionApplicant1Surname = this.questionMap.Applicant1Surname;
      this.questionApplicant1Title = this.questionMap.Applicant1Title;
      this.questionApplicant1Student = this.questionMap.Applicant1Student;
      this.questionSecondApplicantIncluded =
        this.questionMap.SecondApplicantIncluded;
      this.questionApplicant2DoB = this.questionMap.Applicant2DoB;
      this.questionApplicant2Forename = this.questionMap.Applicant2Forename;
      this.questionApplicant2Surname = this.questionMap.Applicant2Surname;
      this.questionApplicant2Title = this.questionMap.Applicant2Title;
      this.questionApplicant2Student = this.questionMap.Applicant2Student;
      this.repeatingGroupPreviousClaim =
        this.questionMap.RepeatingGroupPreviousClaim;
      this.questionPreviousClaim = this.questionMap.PreviousClaim;
      this.questionNoClaimsContents = this.questionMap.NoClaimsContents;
      // cook data
      this.questionApplicant1Student.Value = convertToBool(
        this.questionApplicant1Student.Value
      );
      this.questionApplicant2Student.Value = convertToBool(
        this.questionApplicant2Student.Value
      );
      this.questionPreviousClaim.Value = convertToBool(
        this.questionPreviousClaim.Value
      );
      this.questionSecondApplicantIncluded.Value = convertToBool(
        this.questionSecondApplicantIncluded.Value
      );

      if (this.questionPreviousClaim?.Value === true) {
        this.populateClaimsValue();
      }

      if (!this.questionNoClaimsContents.Value) {
        this.questionNoClaimsContents.Value = numbersToWords(0);
      }

      this.initHeldInsurance();

      this.setupSubmitApplicant2Data();
    },
    populateClaimsValue() {
      this.repeatingGroupPreviousClaim.Value = [];
      populateClaimTable(
        this.$store.state.questions,
        this.quoteResponse.Answers,
        this.repeatingGroupPreviousClaim
      );
    },

    initHeldInsurance() {
      this.questionPreviousHeld.Value =
        this.questionPreviousClaim.Value ||
        this.questionNoClaimsContents.Value !== numbersToWords(0);
    },

    async onSubmit() {
      const isValidForm = await this.$refs.observerFormDetails.validate();
      this.isClaimFormValid = !(
        this.questionPreviousClaim.Value &&
        !this.repeatingGroupPreviousClaim.Value?.length
      );

      if (!isValidForm || !this.isClaimFormValid) {
        setTimeout(() => {
          EventBus.$emit("validation-error", "Validation error occurred");
        }, 100);
        return;
      }

      this.setupSubmitApplicant2Data();
      sessionStorage.removeItem("isNewQuoteFromExpiredQuote");

      this.saveProgress();
      this.refreshSessionAndNextStep(
        ROUTE_NAME.TENANTS_CONTENTS.B2B.PROPERTY_DETAILS
      );
    },

    saveAndExit() {
      this.setupSubmitApplicant2Data();
      this.toggleGlobalSaveAndExitDialog();
    },

    setupSubmitApplicant2Data() {
      if (!this.questionSecondApplicantIncluded.Value) {
        this.questionApplicant2DoB.Value = null;
        this.questionApplicant2Forename.Value = null;
        this.questionApplicant2Surname.Value = null;
        this.questionApplicant2Title.Value = null;
        this.questionApplicant2Student.Value = null;
        return;
      }
      if (!this.questionApplicant2Student.Value) {
        this.questionApplicant2Student.Value = false;
      }
    },

    addTableItems(tableItems) {
      this.repeatingGroupPreviousClaim.Value = tableItems;
    },
    removeTableItems(tableItems) {
      this.repeatingGroupPreviousClaim.Value = tableItems;
    },
    resetNoClaimsTable() {
      this.repeatingGroupPreviousClaim.Value = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.second-applicant-included-field {
  :deep(.v-icon) {
    margin-right: 16px !important;
  }

  :deep(.v-btn) {
    padding: 4px 4px !important;
  }
}
.sub-text {
  font-size: 14px !important;
  font-weight: 400;
}

@media screen and (min-width: $grid-breakpoints-md) {
  .sub-text {
    width: 60% !important;
  }
}
</style>
